import { useEffect, useState } from 'react'
import { EditorContent, useEditor, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import TextAlign from '@tiptap/extension-text-align'
import { Placeholder } from '@tiptap/extensions'
import { MediaPicker } from '../media/MediaPicker'
import { ResizableImage } from './ResizableImage'

/**
 * Tiptap (ProseMirror) — chọn vì xử lý **bộ gõ tiếng Việt** (IME/composition) chuẩn,
 * MIT, headless nên toolbar theo đúng token của mình.
 *
 * Output là HTML. **Server sanitize lại** trước khi lưu (`apps/api/src/lib/sanitize.ts`)
 * — toolbar này chỉ cho những tag nằm trong allowlist đó, để user không thấy format
 * "biến mất" sau khi lưu.
 *
 * Ảnh: xem `ResizableImage.ts` — kéo góc để đổi cỡ, và ảnh là **inline** nên căn lề ảnh
 * đi theo `text-align` của đoạn văn chứa nó.
 *
 * `onChange` chỉ được gọi từ sự kiện `update` của editor, và `content` prop chỉ được
 * đọc lúc mount (hoặc khi `resetKey` đổi) — **không** đồng bộ ngược prop → editor mỗi
 * render. Đó chính là kiểu vòng lặp đã làm hỏng form Payload.
 */
export function RichTextEditor({
  content, onChange, placeholder = 'Viết nội dung…', resetKey,
}: { content: string; onChange: (html: string) => void; placeholder?: string; resetKey?: string | number }) {
  const [pickerOpen, setPickerOpen] = useState(false)

  const editor = useEditor({
    extensions: [
      // Tiptap v3: StarterKit đã gồm Link + Underline — cấu hình ngay trong đây.
      StarterKit.configure({
        heading: { levels: [2, 3, 4] },
        link: { openOnClick: false, autolink: true, HTMLAttributes: { rel: 'nofollow noopener', target: '_blank' } },
      }),
      // `inline: true` để ảnh nằm trong đoạn văn — khi đó căn lề ảnh đi theo `text-align`
      // của đoạn, không phải mở thêm attribute nào cho sanitizer. Xem ResizableImage.ts.
      ResizableImage.configure({ inline: true }),
      // Chỉ heading + paragraph. Cố ý KHÔNG thêm 'image': ảnh đã là inline nên nó ăn
      // theo căn lề của đoạn chứa nó; đưa vào đây chỉ tạo ra `style` thừa trên <img>,
      // mà sanitizer cũng chặn.
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder }),
    ],
    content,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  })

  // Đổi doc (resetKey) → nạp content mới vào editor. Không chạy khi chỉ gõ.
  useEffect(() => {
    if (!editor) return
    if (editor.getHTML() !== content) editor.commands.setContent(content, { emitUpdate: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey, editor])

  if (!editor) return null

  return (
    <div className="rich-text rounded-input border border-border-strong bg-white focus-within:border-primary-700">
      <Toolbar editor={editor} onImage={() => setPickerOpen(true)} />
      <EditorContent editor={editor} className="prose-nhanho text-sm" />
      <MediaPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(docs) => {
          const m = docs[0]
          if (!m) return
          // Chèn kèm kích thước THẬT của biến thể được chọn, không để trống: thiếu
          // width/height thì trang web bị dịch chuyển bố cục (CLS) lúc ảnh tải xong,
          // và người dùng không có gì để kéo cho nhỏ lại.
          const v = m.sizes?.card
          editor.chain().focus().setImage({
            src: v?.url ?? m.url,
            alt: m.alt,
            width: v?.width ?? m.width,
            height: v?.height ?? m.height,
          }).run()
        }}
      />
    </div>
  )
}

function Toolbar({ editor, onImage }: { editor: Editor; onImage: () => void }) {
  const B = ({ on, active, label, title }: { on: () => void; active?: boolean; label: string; title: string }) => (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => { e.preventDefault(); on() }}
      className={`h-7 min-w-7 rounded px-1.5 text-xs font-medium ${active ? 'bg-primary-700 text-white' : 'text-ink hover:bg-primary-50'}`}
    >
      {label}
    </button>
  )
  /**
   * Nhận **cả đường dẫn nội bộ** (`/danh-muc/moc-khoa`) lẫn URL đầy đủ.
   *
   * Link nội bộ phải viết tương đối: `lib/sanitize.ts` phân biệt trong/ngoài bằng dấu `/` đầu
   * chuỗi, và chỉ link nội bộ mới **không** bị gắn `nofollow` — dán URL tuyệt đối của chính
   * site vào thì nó bị coi là link ra ngoài và mất tác dụng liên kết nội bộ.
   */
  const setLink = () => {
    const prev = editor.getAttributes('link').href as string | undefined
    const url = window.prompt('Địa chỉ link — https://… hoặc /danh-muc/… cho link trong site', prev ?? '')
    if (url === null) return
    const href = url.trim()
    if (!href) return void editor.chain().focus().unsetLink().run()
    if (!/^https?:\/\//i.test(href) && !/^\/(?!\/)/.test(href)) {
      return window.alert('Link phải bắt đầu bằng https:// (ra ngoài) hoặc / (trong site)')
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href }).run()
  }
  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-border px-2 py-1.5">
      <B title="Tiêu đề 2" label="H2" active={editor.isActive('heading', { level: 2 })} on={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} />
      <B title="Tiêu đề 3" label="H3" active={editor.isActive('heading', { level: 3 })} on={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} />
      <B title="Tiêu đề 4" label="H4" active={editor.isActive('heading', { level: 4 })} on={() => editor.chain().focus().toggleHeading({ level: 4 }).run()} />
      <span className="mx-1 h-5 w-px bg-border" />
      <B title="Đậm" label="B" active={editor.isActive('bold')} on={() => editor.chain().focus().toggleBold().run()} />
      <B title="Nghiêng" label="I" active={editor.isActive('italic')} on={() => editor.chain().focus().toggleItalic().run()} />
      <B title="Gạch ngang" label="S" active={editor.isActive('strike')} on={() => editor.chain().focus().toggleStrike().run()} />
      <B title="Code" label="</>" active={editor.isActive('code')} on={() => editor.chain().focus().toggleCode().run()} />
      <span className="mx-1 h-5 w-px bg-border" />
      <B title="Danh sách chấm" label="• —" active={editor.isActive('bulletList')} on={() => editor.chain().focus().toggleBulletList().run()} />
      <B title="Danh sách số" label="1. —" active={editor.isActive('orderedList')} on={() => editor.chain().focus().toggleOrderedList().run()} />
      <B title="Trích dẫn" label="❝" active={editor.isActive('blockquote')} on={() => editor.chain().focus().toggleBlockquote().run()} />
      <B title="Đường kẻ" label="—" on={() => editor.chain().focus().setHorizontalRule().run()} />
      <span className="mx-1 h-5 w-px bg-border" />
      {/*
        Căn trái dùng `unsetTextAlign`, không phải `setTextAlign('left')`. Hai lệnh cho ra
        cùng một hiển thị, nhưng `set` ghi `style="text-align: left"` vào MỌI đoạn được
        bấm — mà trái vốn đã là mặc định. Chỉ tổ phình HTML lưu trong DB, không đổi gì.
      */}
      <B title="Căn trái" label="⇤" active={editor.isActive({ textAlign: 'left' })} on={() => editor.chain().focus().unsetTextAlign().run()} />
      <B title="Căn giữa" label="⇹" active={editor.isActive({ textAlign: 'center' })} on={() => editor.chain().focus().setTextAlign('center').run()} />
      <B title="Căn phải" label="⇥" active={editor.isActive({ textAlign: 'right' })} on={() => editor.chain().focus().setTextAlign('right').run()} />
      <B title="Căn đều hai bên" label="≡" active={editor.isActive({ textAlign: 'justify' })} on={() => editor.chain().focus().setTextAlign('justify').run()} />
      <span className="mx-1 h-5 w-px bg-border" />
      <B title="Link" label="🔗" active={editor.isActive('link')} on={setLink} />
      <B title="Chèn ảnh từ thư viện" label="🖼" on={onImage} />
      <span className="mx-1 h-5 w-px bg-border" />
      <B title="Hoàn tác" label="↶" on={() => editor.chain().focus().undo().run()} />
      <B title="Làm lại" label="↷" on={() => editor.chain().focus().redo().run()} />
    </div>
  )
}
